import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import axios from '../../utils/axiosConfig';
import { formattedGlobalDate } from '../../utils/formattedDates';
import SwitchButton3 from '../../componentes/SwitchButton3';
import './formReporteVentas.css';

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        borderColor: state.isFocused ? '#86b7fe' : '#ced4da',
        boxShadow: state.isFocused ? '0 0 0 0.2rem rgba(13, 110, 253, 0.18)' : 'none',
        '&:hover': { borderColor: '#86b7fe' },
    }),
    menuPortal: (base) => ({ ...base, zIndex: 10000 }),
};

const getRecords = (response) => {
    const records = response?.data?.data?.recordset;
    return Array.isArray(records) ? records : [];
};

const getReportRows = (response) => {
    const data = response?.data?.data;
    const records = data?.recordset ?? data?.recordsets ?? data ?? [];
    if (!Array.isArray(records)) return [];
    return Array.isArray(records[0]) ? records[0] : records;
};

const getField = (row, keys) => keys.map((key) => row?.[key]).find((value) => value !== undefined && value !== null && value !== '');
const asFilterValue = (value) => (value === '' || value === null || value === undefined ? 0 : value);

const SearchableSelect = ({ id, value, onChange, options, isLoading }) => (
    <Select
        inputId={id}
        classNamePrefix="reporte-ventas-select"
        options={options}
        value={options.find((option) => String(option.value) === String(value)) ?? null}
        onChange={(option) => onChange(option?.value ?? '')}
        placeholder="Seleccione o busque..."
        noOptionsMessage={() => (isLoading ? 'Cargando opciones...' : 'Sin coincidencias')}
        isClearable
        isSearchable
        isLoading={isLoading}
        styles={selectStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
        menuPosition="fixed"
    />
);

const FormReporteVentas = ({ onSearchStart }) => {
    const [pagado, setPagado] = useState(1);
    const [numeroBusqueda, setNumeroBusqueda] = useState('');
    const [empresa, setEmpresa] = useState('');
    const [acompanante, setAcompanante] = useState('');
    const [categoria, setCategoria] = useState('');
    const [empresas, setEmpresas] = useState([]);
    const [acompanantes, setAcompanantes] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [isCatalogLoading, setIsCatalogLoading] = useState(true);
    const [isSearching, setIsSearching] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [results, setResults] = useState([]);

    const empresaOptions = empresas.map((item) => ({ value: item.IDEmpresasOC, label: item.Descripcion }));
    const acompananteOptions = acompanantes.map((item) => ({ value: item.IDAcompaniante, label: item.Nombre }));
    const categoriaOptions = categorias.map((item) => ({ value: item.id_categoria_oc, label: item.desc_categoria_oc }));

    useEffect(() => {
        let isMounted = true;

        const loadCatalogs = async () => {
            try {
                const requestOptions = {
                    headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
                    withCredentials: true,
                };
                const [acompanantesResult, empresasResult, categoriasResult] = await Promise.allSettled([
                    axios.post(`${process.env.REACT_APP_SERVER}/operaciones2/getacompanantes`, {}, requestOptions),
                    axios.post(`${process.env.REACT_APP_SERVER}/operaciones2/getempresasbusqueda`, {}, requestOptions),
                    axios.post(`${process.env.REACT_APP_SERVER}/operaciones2/getcategoriasoc`, {}, requestOptions),
                ]);

                if (!isMounted) return;
                setAcompanantes(acompanantesResult.status === 'fulfilled' ? getRecords(acompanantesResult.value) : []);
                setEmpresas(empresasResult.status === 'fulfilled' ? getRecords(empresasResult.value) : []);
                setCategorias(categoriasResult.status === 'fulfilled' ? getRecords(categoriasResult.value) : []);

                const failedRequests = [acompanantesResult, empresasResult, categoriasResult]
                    .filter((result) => result.status === 'rejected');
                if (failedRequests.length) {
                    console.error('No se pudieron cargar todos los filtros de Reportes OC:', failedRequests);
                }
            } finally {
                if (isMounted) setIsCatalogLoading(false);
            }
        };

        loadCatalogs();
        return () => { isMounted = false; };
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setHasSearched(true);
        setIsSearching(true);
        setSearchError('');
        setResults([]);
        onSearchStart?.();

        const filtros = {
            NumOCBusqueda: numeroBusqueda.trim(),
            Pagado: pagado,
            IDEmpresasOC: asFilterValue(empresa),
            IDAcompaniante: asFilterValue(acompanante),
            id_categoria_oc: asFilterValue(categoria),
        };
        console.log('Consultando Reportes OC con filtros:', filtros);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_SERVER}/operaciones2/getordenescomprareporte`,
                filtros,
                {
                    headers: { Authorization: `Bearer ${localStorage.getItem('tkn')}` },
                    withCredentials: true,
                }
            );
            console.log('Reporte OC response:', response);/*  */
            setResults(getReportRows(response));
        } catch (error) {
            console.error('Error al consultar Reportes OC:', error);
            setSearchError('No fue posible consultar las órdenes de compra. Intente nuevamente.');
        } finally {
            setIsSearching(false);
        }
    };

    const formatDate = (value) => (value ? formattedGlobalDate(value) || String(value) : '');

    const reportColumns = [
        { key: 'numOCBusqueda', label: '# Busqueda', value: (row) => getField(row, ['numOCBusqueda', 'NumOCBusqueda', 'num', 'NumOC']) },
        { key: 'ContractID', label: 'Contrato', value: (row) => getField(row, ['ContractID', 'contractID']) },
        { key: 'Descripcion', label: 'Descripcion', value: (row) => getField(row, ['Descripcion', 'descripcion']) },
        { key: 'Diferencia', label: 'Diferencia', value: (row) => getField(row, ['Diferencia', 'diferencia']) },
        { key: 'FKLokSolicitudID', label: 'Solicitud', value: (row) => getField(row, ['FKLokSolicitudID', 'fkloksolicitudid']) },
        { key: 'FechaCita', label: 'FechaCita', value: (row) => formatDate(getField(row, ['FechaCita', 'fechaCita'])) },
        { key: 'FechaFin', label: 'FechaFin', value: (row) => formatDate(getField(row, ['FechaFin', 'fechaFin'])) },
        { key: 'Nombre', label: 'Nombre', value: (row) => getField(row, ['Nombre', 'nombre']) },
        { key: 'Observaciones', label: 'Observaciones', value: (row) => getField(row, ['Observaciones', 'Observacion', 'observacion']) },
        { key: 'PlacaTruck', label: 'Placa', value: (row) => getField(row, ['PlacaTruck', 'placaTruck']) },
        { key: 'Ruta', label: 'Ruta', value: (row) => getField(row, ['Ruta', 'ruta']) },
        { key: 'cliente', label: 'cliente', value: (row) => getField(row, ['cliente', 'Cliente']) },
        { key: 'desc_categoria', label: 'Categoria', value: (row) => getField(row, ['desc_categoria', 'DescCategoria', 'desc_categoria_oc']) },
        { key: 'numFacturacion', label: 'Facturacion', value: (row) => getField(row, ['numFacturacion', 'NumFacturacion', 'numFacturaOC']) },
        { key: 'tarifa', label: 'Tarifa', value: (row) => getField(row, ['tarifa', 'Tarifa']) },
        { key: 'user_creation', label: 'Usuario', value: (row) => getField(row, ['user_creation', 'UserCreation', 'user_creacion']) },
    ];

    const handleExportExcel = () => {
        const exportRows = results.map((row) => Object.fromEntries(
            reportColumns.map((column) => [column.label, column.value(row) ?? ''])
        ));
        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        worksheet['!cols'] = reportColumns.map((column) => ({
            wch: column.key === 'Observaciones' ? 70 : column.key === 'Ruta' ? 36 : 22,
        }));
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Reportes OC');
        XLSX.writeFile(workbook, `Reportes_OC_${new Date().toISOString().slice(0, 10)}.xlsx`);
    };

    return (
        <form className="reporte-ventas-form" onSubmit={handleSubmit}>
            <div className="reporte-ventas-fields">
                <label className="reporte-ventas-label" htmlFor="reporte-ventas-pagado">Pagado</label>
                <div className="reporte-ventas-switch">
                    <SwitchButton3
                        checked={pagado === 1}
                        onChange={(isPaid) => setPagado(isPaid ? 1 : 0)}
                        type={<span>{pagado === 1 ? 'Sí' : 'No'}</span>}
                    />
                    <input id="reporte-ventas-pagado" type="hidden" name="pagado" value={pagado} />
                </div>

                <label className="reporte-ventas-label" htmlFor="reporte-ventas-busqueda">Núm. búsqueda</label>
                <div>
                    <input
                        id="reporte-ventas-busqueda"
                        className="form-control"
                        value={numeroBusqueda}
                        onChange={(event) => setNumeroBusqueda(event.target.value)}
                    />
                </div>

                <label className="reporte-ventas-label" htmlFor="reporte-ventas-empresa">Empresa</label>
                <div>
                    <SearchableSelect id="reporte-ventas-empresa" value={empresa} onChange={setEmpresa} isLoading={isCatalogLoading} options={empresaOptions} />
                </div>

                <label className="reporte-ventas-label" htmlFor="reporte-ventas-acompanante">Acompañante</label>
                <div>
                    <SearchableSelect id="reporte-ventas-acompanante" value={acompanante} onChange={setAcompanante} isLoading={isCatalogLoading} options={acompananteOptions} />
                </div>

                <label className="reporte-ventas-label" htmlFor="reporte-ventas-categoria">Categoría</label>
                <div>
                    <SearchableSelect id="reporte-ventas-categoria" value={categoria} onChange={setCategoria} isLoading={isCatalogLoading} options={categoriaOptions} />
                </div>

                <div className="reporte-ventas-actions">
                    <button type="submit" className="btn btn-primary px-4" disabled={isSearching}>
                        {isSearching ? 'Buscando...' : 'Buscar'}
                    </button>
                </div>

                {hasSearched && (
                    <section className="reporte-ventas-results" aria-live="polite">
                    {isSearching && (
                        <p className="reporte-ventas-message"><span className="spinner-border spinner-border-sm me-2" aria-hidden="true" />Consultando órdenes de compra...</p>
                    )}
                    {!isSearching && searchError && <p className="reporte-ventas-message reporte-ventas-message--error">{searchError}</p>}
                    {!isSearching && !searchError && results.length === 0 && <p className="reporte-ventas-message">No se encontraron registros con los filtros seleccionados.</p>}
                    {!isSearching && !searchError && results.length > 0 && (
                        <>
                            <div className="reporte-ventas-table-toolbar">
                                <span>{results.length} {results.length === 1 ? 'registro encontrado' : 'registros encontrados'}</span>
                                <button type="button" className="btn btn-success btn-sm" onClick={handleExportExcel}>
                                    <i className="bx bx-spreadsheet me-1" aria-hidden="true" />Exportar Excel
                                </button>
                            </div>
                            <div className="reporte-ventas-table-wrapper">
                                <table className="table table-hover reporte-ventas-table">
                                <thead>
                                    <tr>
                                        {reportColumns.map((column) => <th key={column.key}>{column.label}</th>)}
                                    </tr>
                                </thead>
                                <tbody>
                                    {results.map((row, index) => (
                                        <tr key={getField(row, ['ID', 'id', 'numOCBusqueda', 'NumOCBusqueda', 'num']) ?? index}>
                                            {reportColumns.map((column) => <td key={column.key}>{column.value(row) ?? '-'}</td>)}
                                        </tr>
                                    ))}
                                </tbody>
                                </table>
                            </div>
                        </>
                    )}
                    </section>
                )}
            </div>
        </form>
    );
};

export default FormReporteVentas;
